// Shared build identity normalization for the runtime artifact and Vite config.
// Vite and native Node need explicit source paths before source-package aliases exist.
import { asRecord } from "../../packages/normalization-core/src/record-coerce.ts";
import { normalizeNullableString } from "../../packages/normalization-core/src/string-coerce.ts";
import { truncateUtf16Safe } from "../../packages/normalization-core/src/utf16-slice.ts";
import type { ControlUiBuildInfo } from "./build-info-types.ts";

const FULL_GIT_SHA = /^[0-9a-f]{40}$/u;
const UTC_BUILD_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/u;
const BUILD_ID_MAX_LENGTH = 96;

function normalizeControlUiCommit(value: unknown): string | null {
  const commit = normalizeNullableString(value)?.toLowerCase() ?? null;
  return commit && FULL_GIT_SHA.test(commit) ? commit : null;
}

function normalizeControlUiBranch(value: unknown): string | null {
  const branch = normalizeNullableString(value);
  return branch && branch !== "HEAD" ? truncateUtf16Safe(branch, 100) : null;
}

function normalizeControlUiBuildTimestamp(value: unknown): string | null {
  const timestamp = normalizeNullableString(value);
  if (!timestamp || !UTC_BUILD_TIMESTAMP.test(timestamp)) {
    return null;
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const canonicalInput = timestamp.replace(/(?:\.(\d{1,3}))?Z$/u, (_match, fraction) => {
    return `.${String(fraction ?? "").padEnd(3, "0")}Z`;
  });
  return date.toISOString() === canonicalInput ? date.toISOString() : null;
}

function normalizeControlUiBuildId(value: unknown): string {
  const normalized = normalizeNullableString(value)?.replace(/[^a-zA-Z0-9._-]+/g, "-");
  return normalized?.slice(0, BUILD_ID_MAX_LENGTH) || "dev";
}

export function normalizeControlUiBuildInfo(value: unknown): ControlUiBuildInfo {
  const record = asRecord(value);
  const version = normalizeNullableString(record.version);
  const commit = normalizeControlUiCommit(record.commit);
  const builtAt = normalizeControlUiBuildTimestamp(record.builtAt);
  const release = record.release === true;
  return {
    version,
    commit,
    builtAt,
    release,
    commitAt: normalizeControlUiBuildTimestamp(record.commitAt),
    branch: normalizeControlUiBranch(record.branch),
    dirty: typeof record.dirty === "boolean" ? record.dirty : null,
    buildId: normalizeControlUiBuildId(
      record.buildId ??
        [version, release ? "release" : null, commit?.slice(0, 12), builtAt]
          .filter(Boolean)
          .join("-"),
    ),
  };
}
